import { inject } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { OnboardingFamilyConstants as ui } from '../../Config/UIConstants';
import { CommonActionButton } from '../../SharedComponents/CommonActionButton';
import { CommonTextInput } from '../../SharedComponents/CommonTextInput';
import { FullScreenModalContainer } from '../../SharedComponents/FullScreenModalContainer';
import { IInviteInformation } from '../../Stores/OnboardingStore';
import { Routes } from '../../_controllers/RouteController';
import { ApplicationStore } from '../../_stores/ApplicationStore';
import { colors } from '../../styles/colors';
import { largeShadow } from '../../styles/styleObjects';
import { validateEmail } from '../../util/validateEmail';
import { WarningText } from '../AuthPages/WarningText';

interface IState {
    invites: Array<IInviteInformation>;
    familyName: string;
    checkInputs: boolean;
}

interface IProps {
    store: ApplicationStore;
}

@inject('store')
export class OnboardingAddFamily extends React.Component<RouteComponentProps<any> & IProps, IState> {
    public state: IState = {
        invites: new Array(3).fill('').map(() => ({ firstName: '', email: '' })),
        familyName: '',
        checkInputs: false,
    };

    private onPressNext = (): void => {
        this.setState({ checkInputs: true }, async () => {
            if (this.state.familyName !== '' && this.invitesAreValid()) {
                const validInvites = this.getValidInvites();
                this.props.store.onboardingStore.setFamilyInformation(this.state.familyName, validInvites);
                await this.props.store.onboardingStore.createFamilyAndStory();
                await this.props.store.onboardingStore.addAllPhotosToStory();
                this.props.history.push(Routes.ONBOARDING_FINISHED);
            }
        });
    }

    private onPressSkip = async (): Promise<void> => {
        const storyTitle = this.props.store.onboardingStore.storyTitle;
        if (storyTitle !== '' && storyTitle) {
            await this.props.store.onboardingStore.createStory();
            this.props.store.onboardingStore.addAllPhotosToStory();
            this.props.history.push(Routes.ONBOARDING_FINISHED);
        } else {
            this.props.history.push(Routes.ONBOARDING_FINISHED);
        }
    }

    private invitesAreValid(): boolean {
        if (!this.state.checkInputs) {
            return true;
        }

        const validInvites = this.getValidInvites();
        return validInvites.length !== 0;
    }

    private getValidInvites(): Array<IInviteInformation> {
        return this.state.invites.filter(
          invite => invite.email !== '' && invite.firstName !== '' && validateEmail(invite.email),
        );
    }
    private renderFamilyInviteInputs(type: 'firstName' | 'email', index: number) {
        let header = type === 'firstName' ? ui.FIRST_NAME_HEADER : ui.EMAIL_HEADER;
        let placeholder = index > 0 ? ui.OPTIONAL_PLACEHOLDER : ui.REQUIRED_PLACEHOLDER;

        return (
            <div>
                {index === 0 && <WarningText isValid={this.invitesAreValid()} text={ui.REQUIRED_PLACEHOLDER} />}
                <CommonTextInput
                    value={this.state.invites[index][type]}
                    containerStyle={styles.inputContainer}
                    onChangeText={(text: string) => {
                        let state = { ...this.state };
                        state.invites[index][type] = text;
                        this.setState({ invites: state.invites });
                    }}
                    header={header}
                    placeholder={placeholder}
                />
            </div>
        );
    }

    public render() {
        return (
            <FullScreenModalContainer
                justifyContent="space-between"
                alignItems="center"
                {...this.props}
                onPressExit={() => this.props.history.push(Routes.ALBUMS)}
                style={styles.minHeight}
            >
                <p style={styles.headerText}>{ui.HEADER}</p>
                <div style={styles.contentContainer}>
                    <p style={styles.header}>{ui.SUB_HEADER}</p>
                    <div>
                        <WarningText
                            text={ui.REQUIRED_PLACEHOLDER}
                            isValid={this.state.checkInputs ? this.state.familyName !== '' : true}
                        />
                        <CommonTextInput
                            value={this.state.familyName}
                            containerStyle={styles.topInput}
                            onChangeText={(text: string) => {
                                this.setState({ familyName: text });
                            }}
                            header={ui.FAMILY_NAME_HEADER}
                            placeholder={ui.REQUIRED_PLACEHOLDER}
                        />
                    </div>

                    <p style={styles.subHeader}>{ui.FAMILY_MEMBERS}</p>

                    <p style={styles.bottomSubText}>{ui.BOTTOM_TEXT}</p>
                    <div style={styles.inputsContainer}>
                        {/* TODO: Write this so tab goes from first name to email */}
                        <div style={styles.leftInputsContainer}>
                            {this.renderFamilyInviteInputs('firstName', 0)}
                            {this.renderFamilyInviteInputs('firstName', 1)}
                            {this.renderFamilyInviteInputs('firstName', 2)}
                        </div>
                        <div style={styles.rightInputsContainer}>
                            {this.renderFamilyInviteInputs('email', 0)}
                            {this.renderFamilyInviteInputs('email', 1)}
                            {this.renderFamilyInviteInputs('email', 2)}
                        </div>
                    </div>
                </div>
                <div style={styles.bottomContainer}>
                    <div style={styles.flexRow}>
                        <CommonActionButton
                            buttonContent={ui.SKIP}
                            containerStyle={{ ...styles.buttonContainer, backgroundColor: colors.CANCEL_TEXT }}
                            fontStyle={styles.buttonText}
                            onClick={this.onPressSkip}
                        />
                        <div style={styles.widthBuffer} />
                        <CommonActionButton
                            buttonContent={ui.CREATE_FAMILY}
                            containerStyle={styles.buttonContainer}
                            fontStyle={styles.buttonText}
                            onClick={this.onPressNext}
                        />
                    </div>
                </div>
            </FullScreenModalContainer>
        );
    }
}

const styles = {
    minHeight: { minHeight: 850 },
    widthBuffer: {
        width: 20,
    },
    flexRow: {
        display: 'flex',
        flexDirection: 'row',
    } as React.CSSProperties,
    rightInputsContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: 300,
        width: 400,
        marginLeft: 15,
    } as React.CSSProperties,
    leftInputsContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: 300,
        width: 400,
        marginRight: 15,
    } as React.CSSProperties,
    inputsContainer: {
        display: 'flex',
        width: 800,
    },
    bottomSubText: {
        fontSize: 18,
        marginBottom: 15,
        width: '60%',
        textAlign: 'center',
    } as React.CSSProperties,
    topSubText: {
        fontSize: 18,
        marginBottom: 3,
    },
    header: {
        marginBottom: 10,
        fontSize: 18,
    } as React.CSSProperties,
    subHeader: {
        fontSize: 22,
        marginBottom: 10,
        marginTop: 10,
        color: 'black',
        fontWeight: 900,
    } as React.CSSProperties,
    inputContainer: {
        width: '100%',
        height: 75,
        marginBottom: 15,
        backgroundColor: 'white',
        boxShadow: largeShadow,
    },
    topInput: {
        width: 400,
        height: 75,
        marginBottom: 15,
        backgroundColor: 'white',
        boxShadow: largeShadow,
    },
    headerText: {
        fontSize: 25,
        color: colors.DARK_TEXT,
        marginTop: 50,
        marginBottom: 30,
    },
    contentContainer: {
        display: 'flex',
        width: '100%',
        height: '50%',
        flex: 1,
        flexDirection: 'column',
        alignItems: 'center',
    } as React.CSSProperties,
    bottomContainer: {
        height: 150,
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
    } as React.CSSProperties,
    buttonText: {
        fontSize: 14,
        fontWeight: 200,
        letterSpacing: 1,
    } as React.CSSProperties,
    buttonContainer: {
        height: 48,
        width: 180,
    },
};



// WEBPACK FOOTER //
// ./src/Pages/OnboardingPages/OnboardingAddFamily.tsx